import React from "react";
import { AlertCircle, Flame } from "lucide-react";
import Button from "../../../components/ui/Button";
import { useTranslation } from "react-i18next";

interface ImportantInformationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onUnderstand: () => void;
}

const ImportantInformationModal: React.FC<ImportantInformationModalProps> = ({
    isOpen,
    onClose,
    onUnderstand,
}) => {
    const { t } = useTranslation();
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                {/* Header */}
                <div className="flex items-center justify-center mb-6">
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mr-3">
                        <AlertCircle className="w-5 h-5 text-white" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900">{t("importantInformationModal.title")}</h2>
                </div>

                {/* Content */}
                <div className="space-y-4">
                    {/* Private Individual Section */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <h3 className="font-bold text-green-700 mb-2">{t("importantInformationModal.privateIndividual")}</h3>
                        <ul className="text-green-700 space-y-1 text-sm">
                            <li>• {t("importantInformationModal.canPurchaseQuotes")}</li>
                            <li>• {t("importantInformationModal.maxQuotePurchases")} <span className="font-bold">{t("importantInformationModal.maxQuotePurchasesAmount")}</span></li>
                            <li>• {t("importantInformationModal.maxAnnualTurnover")} <span className="font-bold">{t("importantInformationModal.maxAnnualTurnoverAmount")}</span></li>
                            <li className="text-xs">{t("importantInformationModal.annualTurnoverNote")}</li>
                        </ul>
                    </div>

                    {/* VAT Holder Section */}
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-center mb-2">
                            <Flame className="w-4 h-4 text-red-600 mr-2" />
                            <h3 className="font-bold text-red-700">{t("importantInformationModal.vatHolder")}</h3>
                        </div>
                        <p className="text-red-700 text-sm mb-2">
                            {t("importantInformationModal.completeCompanyInfo")} <span className="font-bold">{t("importantInformationModal.companyInformationSection")}</span> {t("importantInformationModal.sectionToUnlock")}
                        </p>
                        <ul className="text-red-700 space-y-1 text-sm">
                            <li>• {t("importantInformationModal.noLimits")}</li>
                            <li>• {t("importantInformationModal.attractMoreCustomers")}</li>
                            <li>• {t("importantInformationModal.professionalProfile")}</li>
                        </ul>
                    </div>
                </div>

                {/* Footer */}
                <div className="mt-6 flex justify-center">
                    <Button
                        onClick={onUnderstand}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-medium"
                    >
                        {t("importantInformationModal.iUnderstand")}
                    </Button>
                </div>

                {/* Close button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
        </div>
    );
};

export default ImportantInformationModal;
